/**
 * ChainVault WP - Main App Component
 *
 * Supports both full wallet mode and single-page shortcode mode.
 * When [chainvault_market] or [chainvault_apps] is used, the app
 * reads the data-page attribute and shows only that page.
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { WalletProvider, useWallet } from './context/WalletContext';
import Navbar from './components/Navbar';
import RosieChat from './components/RosieChat';
import Onboarding from './components/Onboarding';
import WalletPage from './pages/WalletPage';
import ExchangePage from './pages/ExchangePage';
import TransactionsPage from './pages/TransactionsPage';
import LaunchpadPage   from './pages/LaunchpadPage';
import ERC20CreatorPage from './pages/ERC20CreatorPage';
import SPLCreatorPage  from './pages/SPLCreatorPage';
import MultisenderPage from './pages/MultisenderPage';
import EarnPage       from './pages/EarnPage';
import BuyPage        from './pages/BuyPage';
import IDODetailPage   from './pages/IDODetailPage';
import PredictionPage  from './pages/PredictionPage';
import P2PPage         from './pages/P2PPage';
import AppsPage from './pages/AppsPage';
import VaultMarketPage from './pages/VaultMarketPage';
import CoinDetailPage from './pages/CoinDetailPage';
import WalletAssetDetailPage from './pages/WalletAssetDetailPage';
import NewsArticlePage from './pages/NewsArticlePage';
import SettingsPage  from './pages/SettingsPage';
import Footer        from './components/Footer';
import RosiePage       from './pages/RosiePage';

const toastOptions = {
    duration: 4000,
    style: {
        background:   '#1a1a2e',
        color:        '#fff',
        borderRadius: '10px',
        border:       '1px solid #2a2a4a',
    },
};

/**
 * Read the data-page attribute from the WordPress shortcode container.
 * Returns the page name if a specific shortcode was used, or null for full wallet.
 */
function getShortcodePage() {
    const root = document.getElementById( 'chainvault-wallet-root' );
    if ( ! root ) return null;
    const page = root.getAttribute( 'data-page' );
    if ( ! page || page === 'wallet' ) return null;
    return page;
}

/**
 * Single page mode — shows one page without navbar or routing.
 * Used when [chainvault_market], [chainvault_apps] etc. are used.
 */
function SinglePageApp( { page } ) {
    const pageMap = {
        'exchange':  <ExchangePage />,
        'launchpad': <LaunchpadPage />,
        'apps':      <AppsPage />,
        'market':    <VaultMarketPage />,
    };

    const component = pageMap[ page ];

    if ( ! component ) return null;

    return (
        <div className="chainvault-app">
            { component }
            <RosieChat />
        <Toaster position="top-right" toastOptions={ toastOptions } />
        </div>
    );
}

function BottomNav() {
    const location = useLocation();
    const isActive = ( path ) => {
        if ( path === '/' ) return location.pathname === '/';
        return location.pathname.startsWith( path );
    };
    const navLabels = window.chainvaultSettings?.branding || {};
    return (
        <nav className="cv-bottom-nav">
            <Link to="/" className={ `cv-bnav-item ${ isActive('/') ? 'active' : '' }` }>
                <i className="ti ti-wallet" aria-hidden="true"></i>
                <span>Wallet</span>
            </Link>
            <Link to="/market" className={ `cv-bnav-item ${ isActive('/market') ? 'active' : '' }` }>
                <i className="ti ti-chart-candle" aria-hidden="true"></i>
                <span>Market</span>
            </Link>
            <Link to="/rosie" className={ `cv-bnav-center ${ isActive('/rosie') ? 'active' : '' }` } aria-label="Rosie AI">
                <i className="ti ti-robot" aria-hidden="true"></i>
            </Link>
            <Link to="/predict" className={ `cv-bnav-item ${ isActive('/predict') ? 'active' : '' }` }>
                <i className="ti ti-trending-up" aria-hidden="true"></i>
                <span>Predict</span>
            </Link>
            <Link to="/launchpad" className={ `cv-bnav-item ${ isActive('/launchpad') ? 'active' : '' }` }>
                <i className="ti ti-rocket" aria-hidden="true"></i>
                <span>Launch</span>
            </Link>
        </nav>
    );
}

function InnerApp() {
    const {
        showOnboarding,
        handleBuiltInWalletCreated,
        connectMetaMask,
        connectPhantomWallet,
        setShowOnboarding,
    } = useWallet();

    /* ── Push Notification Polling ── */
    React.useEffect( () => {
        const settings = window.chainvaultSettings || {};
        if ( settings.pushEnabled !== '1' ) return;
        const apiBase = settings.siteUrl || '/wp-json/chainvault/v1';

        /* Register subscriber */
        const registerSub = () => {
            const subId = localStorage.getItem('cv_push_sub_id') || ('sub_' + Date.now() + '_' + Math.random().toString(36).substr(2,9));
            localStorage.setItem('cv_push_sub_id', subId);
            fetch( apiBase + '/push/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ endpoint: subId, p256dh: 'polling', auth: 'polling', userAgent: navigator.userAgent }),
            }).catch(()=>{});
        };

        if ( Notification.permission === 'granted' ) {
            registerSub();
        } else {
            Notification.requestPermission().then( p => { if(p==='granted') registerSub(); }).catch(()=>{});
        }

        /* Check user-specific notifications (win/loss) */
        const checkUserNotif = () => {
            const s2       = window.chainvaultSettings || {};
            const apiBase2 = s2.siteUrl || '/wp-json/chainvault/v1';
            const stored   = localStorage.getItem('chainvault_accounts');
            const accounts = stored ? JSON.parse(stored) : [];
            const activeIdx = parseInt(localStorage.getItem('chainvault_active_account') || '0');
            const acct     = accounts[activeIdx];
            if (!acct) return;
            const address  = acct.solanaAddress || acct.ethereumAddress || '';
            if (!address) return;
            fetch(apiBase2 + '/user/notifications?address=' + encodeURIComponent(address))
                .then(r => r.json())
                .then(notifs => {
                    if (!notifs || !notifs.length) return;
                    const existing = JSON.parse(localStorage.getItem('cv_notifs') || '[]');
                    const combined = [...notifs, ...existing].slice(0, 10);
                    localStorage.setItem('cv_notifs', JSON.stringify(combined));
                    window.dispatchEvent(new Event('cv_new_notif'));
                    try {
                        const ctx  = new (window.AudioContext || window.webkitAudioContext)();
                        const osc  = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.connect(gain); gain.connect(ctx.destination);
                        osc.frequency.setValueAtTime(880,  ctx.currentTime);
                        osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
                        osc.frequency.setValueAtTime(880,  ctx.currentTime + 0.2);
                        gain.gain.setValueAtTime(0.3, ctx.currentTime);
                        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
                        osc.start(ctx.currentTime);
                        osc.stop(ctx.currentTime + 0.4);
                    } catch(e) {}
                }).catch(() => {});
        };

        /* ── Transaction notification helper ── */
        window.cvNotifyTx = ( type, amount, token, address ) => {
            const icon   = type === 'sent' ? '📤' : '📥';
            const title  = type === 'sent' ? 'Sent Successfully' : 'Received';
            const msg    = type === 'sent'
                ? `You sent ${ amount } ${ token } to ${ address.slice(0,8) }...${ address.slice(-4) }`
                : `You received ${ amount } ${ token } from ${ address.slice(0,8) }...${ address.slice(-4) }`;
            const notifs = JSON.parse( localStorage.getItem('cv_notifs') || '[]' );
            notifs.unshift({ title: icon + ' ' + title, message: msg, url: '', time: Date.now(), read: false });
            if ( notifs.length > 10 ) notifs.splice(10);
            localStorage.setItem( 'cv_notifs', JSON.stringify(notifs) );
            window.dispatchEvent( new Event('cv_new_notif') );
            try {
                const ctx  = new (window.AudioContext || window.webkitAudioContext)();
                const osc  = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain); gain.connect(ctx.destination);
                osc.frequency.setValueAtTime( type === 'sent' ? 660 : 880, ctx.currentTime );
                osc.frequency.setValueAtTime( type === 'sent' ? 440 : 1100, ctx.currentTime + 0.15 );
                gain.gain.setValueAtTime( 0.3, ctx.currentTime );
                gain.gain.exponentialRampToValueAtTime( 0.001, ctx.currentTime + 0.4 );
                osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.4);
            } catch(e) {}
        };

        /* Check for new notifications */
        const checkNotif = () => {
            fetch( apiBase + '/push/pending' )
                .then( r => r.json() )
                .then( data => {
                    if ( !data || !data.title ) return;
                    const notifId = data.title + '_' + ( data.sent_at || '' );
                    const lastId  = localStorage.getItem('cv_last_notif') || '';
                    if ( notifId === lastId ) return;
                    localStorage.setItem( 'cv_last_notif', notifId );
                    const notifs = JSON.parse( localStorage.getItem('cv_notifs') || '[]' );
                    notifs.unshift({ title: data.title, message: data.message, url: data.url, time: Date.now(), read: false });
                    if ( notifs.length > 10 ) notifs.splice(10);
                    localStorage.setItem( 'cv_notifs', JSON.stringify(notifs) );
                    window.dispatchEvent( new Event('cv_new_notif') );
                    try {
                        const ctx  = new ( window.AudioContext || window.webkitAudioContext )();
                        const osc  = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.connect(gain); gain.connect(ctx.destination);
                        osc.frequency.setValueAtTime( 880,  ctx.currentTime );
                        osc.frequency.setValueAtTime( 1100, ctx.currentTime + 0.1 );
                        osc.frequency.setValueAtTime( 880,  ctx.currentTime + 0.2 );
                        gain.gain.setValueAtTime( 0.3, ctx.currentTime );
                        gain.gain.exponentialRampToValueAtTime( 0.001, ctx.currentTime + 0.4 );
                        osc.start( ctx.currentTime );
                        osc.stop( ctx.currentTime + 0.4 );
                    } catch(e) {}
                }).catch(()=>{});
        };

        checkNotif();
        checkUserNotif();
        const interval = setInterval( () => { checkNotif(); checkUserNotif(); }, 60000 );
        return () => clearInterval( interval );
    }, [] );

    const handleConnectExternal = () => { setShowOnboarding( false ); };

    // Check if a specific page shortcode was used
    const shortcodePage = getShortcodePage();

    // If a specific shortcode like [chainvault_market] was used,
    // show only that page — no navbar, no onboarding, no routing
    if ( shortcodePage ) {
        return <SinglePageApp page={ shortcodePage } />;
    }

    // Full wallet mode — show onboarding if needed
    if ( showOnboarding ) {
        return (
            <>
                <Onboarding
                    onComplete={ handleBuiltInWalletCreated }
                    onConnect={ handleConnectExternal }
                    connectMetaMask={ connectMetaMask }
                    connectPhantomWallet={ connectPhantomWallet }
                />
                { window.innerWidth > 768 && <RosieChat /> }
        <Toaster position="top-right" toastOptions={ toastOptions } />
            </>
        );
    }

    // Full wallet mode — show everything
    return (
        <Router basename="/wallet">
            <div className="chainvault-app">
                <Navbar />
                <main className="chainvault-main">
                    <Routes>
                        <Route path="/"             element={ <WalletPage />       } />
                        <Route path="/transactions" element={ <TransactionsPage /> } />
                        <Route path="/exchange"     element={ <ExchangePage />     } />
                        <Route path="/rosie"        element={ <RosiePage />        } />
                        <Route path="/launchpad"    element={ <LaunchpadPage />    } />
                        <Route path="/launchpad/create-erc20" element={ <ERC20CreatorPage /> } />
                        <Route path="/launchpad/create-spl" element={ <SPLCreatorPage /> } />
                        <Route path="/launchpad/multisender" element={ <MultisenderPage /> } />
                        <Route path="/earn" element={ <EarnPage /> } />
                        <Route path="/buy" element={ <BuyPage /> } />
                        <Route path="/launchpad/ido/:slug" element={ <IDODetailPage /> } />
                        <Route path="/predict"      element={ <PredictionPage />   } />
                        <Route path="/p2p"         element={ <P2PPage />          } />
                        <Route path="/apps"         element={ <AppsPage />         } />
                        <Route path="/market"       element={ <VaultMarketPage />  } />
                        <Route path="/market/coin/:coinId" element={ <CoinDetailPage /> } />
                        <Route path="/wallet/asset/:coinId" element={ <WalletAssetDetailPage /> } />
                        <Route path="/market/news/:articleId" element={ <NewsArticlePage /> } />
                        <Route path="/settings"     element={ <SettingsPage />     } />
                    </Routes>
                </main>
                { window.innerWidth > 768 && <RosieChat /> }
        <Toaster position="top-right" toastOptions={ toastOptions } />
                <BottomNav />
            </div>
                    { /* Referral tracking */ }
                    { (function() {
                        const ref = new URLSearchParams(window.location.search).get('ref');
                        if(ref) localStorage.setItem('cv_referral_code', ref);
                        return null;
                    }()) }
                    <Footer />
                    { /* Push Notifications */ }
                    { (function() {
                        const settings = window.chainvaultSettings || {};
                        if ( settings.pushEnabled === '1' && 'serviceWorker' in navigator && 'PushManager' in window ) {
                            /* Request notification permission and register subscriber */
                            Notification.requestPermission().then(perm => {
                                if(perm !== 'granted') return;
                                /* Save subscriber to WordPress */
                                const subId = localStorage.getItem('cv_push_sub_id') || ('sub_' + Date.now() + '_' + Math.random().toString(36).substr(2,9));
                                localStorage.setItem('cv_push_sub_id', subId);
                                fetch((settings.siteUrl||'/wp-json/chainvault/v1') + '/push/subscribe', {
                                    method: 'POST',
                                    headers: {'Content-Type':'application/json'},
                                    body: JSON.stringify({
                                        endpoint:  subId,
                                        p256dh:    'polling',
                                        auth:      'polling',
                                        userAgent: navigator.userAgent,
                                    })
                                }).catch(console.error);
                            }).catch(console.error);

                            /* Poll for pending notifications every 5 minutes */
                            setInterval(() => {
                                fetch((settings.siteUrl||'/wp-json/chainvault/v1') + '/push/pending')
                                    .then(r=>r.json())
                                    .then(data => {
                                        if(!data || !data.title) return;
                                        /* Deduplicate using sent_at timestamp */
                                        var notifId = data.title + '_' + (data.sent_at||'');
                                        var lastId  = localStorage.getItem('cv_last_notif') || '';
                                        if(notifId === lastId) return;
                                        localStorage.setItem('cv_last_notif', notifId);
                                        /* store for bell display */
                                        var notifs = JSON.parse(localStorage.getItem('cv_notifs')||'[]');
                                        notifs.unshift({title:data.title, message:data.message, url:data.url, time:Date.now(), read:false});
                                        if(notifs.length>10) notifs=notifs.slice(0,10);
                                        localStorage.setItem('cv_notifs', JSON.stringify(notifs));
                                        window.dispatchEvent(new Event('cv_new_notif'));
                                        /* Play notification sound */
                                        try {
                                            var ctx = new (window.AudioContext || window.webkitAudioContext)();
                                            var osc = ctx.createOscillator();
                                            var gain = ctx.createGain();
                                            osc.connect(gain);
                                            gain.connect(ctx.destination);
                                            osc.frequency.setValueAtTime(880, ctx.currentTime);
                                            osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
                                            osc.frequency.setValueAtTime(880, ctx.currentTime + 0.2);
                                            gain.gain.setValueAtTime(0.3, ctx.currentTime);
                                            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
                                            osc.start(ctx.currentTime);
                                            osc.stop(ctx.currentTime + 0.4);
                                        } catch(e) {}
                                        /* show browser notification via service worker */
                                        if(Notification.permission === 'granted') {
                                            navigator.serviceWorker.ready.then(reg => {
                                                reg.showNotification(data.title, {
                                                    body:  data.message,
                                                    icon:  data.icon || '',
                                                    data:  {url: data.url||window.location.href}
                                                });
                                            }).catch(() => {
                                                /* fallback */
                                                try { new Notification(data.title, {body: data.message}); } catch(e){}
                                            });
                                        }
                                    }).catch(()=>{});
                            }, 300000);
                        }
                        return null;
                    }()) }
        </Router>
    );
}

function App() {
    return (
        <WalletProvider>
            <InnerApp />
        </WalletProvider>
    );
}

export default App;
